<template>
  <div class="roles-container">
    <base-loader v-if="dataIsLoading" />

    <template v-else>
      <base-nav
        title="Роли и права"
        :show-back="true"
        :heading-level="3"
        back-fallback="Settings"
      >
        <base-primary-button
          class="nav-btn"
          @click="toggleCreateForm"
        >
          {{ showCreateForm ? "Отмена" : "Создать роль" }}
        </base-primary-button>
      </base-nav>

      <transition name="fade-slide">
        <div v-if="showCreateForm" class="card form-wrap">
          <h4 class="form-title">Создание роли</h4>
          <PermissionRoleForm
            :is-loading="isLoading"
            @save="handleCreate"
            @cancel="showCreateForm = false"
          />
        </div>
      </transition>

      <div v-if="roles.length === 0" class="no-data">Нет ролей</div>

      <ul v-else class="roles-list">
        <BaseSettingsListItemCard
          v-for="role in localRoles"
          :key="role.id"
          :is-editing="editingRoleId === role.id"
          :is-loading="isLoading"
          edit-title="Редактирование роли"
          :show-toggle="false"
          :show-view="true"
          :show-edit="!role.system"
          :show-delete="!role.system && !role.has_users"
          :delete-message="`Роль «${role.name}» будет удалена безвозвратно.`"
          @view="goToRole(role.id)"
          @edit="startEdit(role)"
          @delete="handleDelete(role.id)"
        >

          <div class="role-top">
            <span class="role-name">{{ role.name }}</span>
            <span class="role-code">{{ role.code }}</span>
            <span v-if="role.system" class="role-badge role-badge--system">
              Системная
            </span>
          </div>
          <div v-if="role.new_permissions_strategy" class="role-strategy">
            Стратегия: {{ strategyLabel(role.new_permissions_strategy) }}
          </div>

          <template #edit>
            <PermissionRoleForm
              :initial-data="role"
              :is-loading="isLoading"
              @save="(payload) => handleUpdate(role.id, payload)"
              @cancel="editingRoleId = null"
            />
          </template>
        </BaseSettingsListItemCard>
      </ul>

      <pagination-component
        v-if="pagination.pages > 1 && !dataIsLoading"
        :page="pagination.page"
        :pages="pagination.pages"
        :series="pagination.series"
      />
    </template>

  </div>
</template>

<script>
import { mapState, mapActions } from "pinia";

import { usePermissionRolesStore } from "@/stores/permissionRolesStore";
import { useNotificationStore } from "@/stores/notificationStore";

import PermissionRoleForm from "@/components/PermissionRole/PermissionRoleForm.vue";
import PaginationComponent from "@/components/PaginationComponent.vue";

const STRATEGY_LABELS = {
  manual: "Только вручную",
  safe_actions: "Безопасные автоматически",
  full_access: "Все новые права автоматически",
};

export default {
  name: "PermissionRolesPage",

  components: {
    PermissionRoleForm,
    PaginationComponent,
  },

  data() {
    return {
      dataIsLoading: true,
      showCreateForm: false,
      editingRoleId: null,
      localRoles: [],
    };
  },

  computed: {
    ...mapState(usePermissionRolesStore, [
      "roles",
      "isLoading",
      "pagination",
    ]),

    notificationStore() {
      return useNotificationStore();
    },
  },

  watch: {
    "$route.query.page": {
      handler(newPage) {
        this.loadRoles(newPage);
      },
    },
  },

  methods: {
    ...mapActions(usePermissionRolesStore, [
      "fetchRoles",
      "createRole",
      "updateRole",
      "deleteRole",
    ]),

    strategyLabel(value) {
      return STRATEGY_LABELS[value] || value;
    },

    async loadRoles(page) {
      const pageNumber =
        parseInt(page) || parseInt(this.$route.query.page) || 1;
      try {
        this.dataIsLoading = true;
        await this.fetchRoles({ page: pageNumber });
        this.localRoles = [...this.roles];
      } finally {
        this.dataIsLoading = false;
      }
    },

    toggleCreateForm() {
      this.showCreateForm = !this.showCreateForm;
      if (this.showCreateForm) this.editingRoleId = null;
    },

    startEdit(role) {
      this.editingRoleId = role.id;
      this.showCreateForm = false;
    },

    goToRole(id) {
      this.$router.push({
        name: "PermissionRoleDetails",
        params: { id },
      });
    },

    async handleCreate(payload) {
      try {
        await this.createRole(payload);
        this.localRoles = [...this.roles];
        this.showCreateForm = false;
        this.notificationStore.showSuccess("Роль создана");
      } catch {
        // обрабатывается в сторе
      }
    },

    async handleUpdate(id, payload) {
      try {
        await this.updateRole(id, payload);
        this.localRoles = [...this.roles];
        this.editingRoleId = null;
        this.notificationStore.showSuccess("Роль обновлена");
      } catch {
        // обрабатывается в сторе
      }
    },

    async handleDelete(id) {
      try {
        await this.deleteRole(id);
        this.localRoles = [...this.roles];
        this.notificationStore.showSuccess("Роль удалена");
      } catch {
        // обрабатывается в сторе
      }
    },
  },

  async created() {
    await this.loadRoles(this.$route.query.page);
  },
};
</script>

<style scoped>
.roles-container {
  flex-grow: 1;
}

.form-wrap {
  margin-bottom: var(--base-out-indent);
}

.form-title {
  margin-bottom: var(--base-in-indent);
}

.roles-list {
  display: flex;
  flex-direction: column;
  gap: var(--base-out-indent);
  margin-bottom: 15px;
}

.role-top {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.role-name {
  font-weight: 600;
  font-size: 17px;
}

.role-code {
  color: #666;
  font-size: 13px;
  font-family: monospace;
}

.role-strategy {
  font-size: 13px;
  color: #888;
}

.role-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.role-badge--system {
  background: #f3e8ff;
  color: #7c3aed;
}


@media (max-width: 768px) {
  .role-top {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
